import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  ServiceRequestsFiltersQuery,
  ChangeServiceRequest,
  CreateServiceRequest,
  ServiceRequestsQuery,
  ServiceRequestsState,
} from "./models/models";
import serviceRequestsApi from "./api/serviceRequestsApi";

export const getServiceRequests = createAsyncThunk(
  "customerServicingSlice/getServiceRequests",
  async (query: ServiceRequestsQuery) => {
    return serviceRequestsApi.getServiceRequests(query);
  }
);

export const getServiceRequest = createAsyncThunk(
  "customerServicingSlice/getServiceRequest",
  async (id: string) => {
    return serviceRequestsApi.getServiceRequest(id);
  }
);

export const createServiceRequest = createAsyncThunk(
  "customerServicingSlice/createServiceRequest",
  async (model: CreateServiceRequest) => {
    return serviceRequestsApi.createServiceRequest(model);
  }
);

export const changeServiceRequest = createAsyncThunk(
  "customerServicingSlice/changeServiceRequest",
  async ({ serviceRequestId: id, model }: { serviceRequestId: string, model: ChangeServiceRequest }) => {
    return serviceRequestsApi.changeServiceRequest(id, model);
  }
);

export const getServiceRequestsFilters = createAsyncThunk(
  "customerServicingSlice/getServiceRequestsFilters",
  async (query: ServiceRequestsFiltersQuery) => {
    return serviceRequestsApi.getServiceRequestsFilters(query);
  }
);

export const publishServiceRequest = createAsyncThunk(
  "customerServicingSlice/publishServiceRequest",
  async ({ id, visibilityCode, targetPartnerIds }: { id: string; visibilityCode?: number; targetPartnerIds?: string[] }) => {
    return serviceRequestsApi.publishServiceRequest(id, visibilityCode, targetPartnerIds);
  }
);

export const unpublishServiceRequest = createAsyncThunk(
  "customerServicingSlice/unpublishServiceRequest",
  async (id: string) => {
    return serviceRequestsApi.unpublishServiceRequest(id);
  }
);

export const archiveServiceRequest = createAsyncThunk(
  "customerServicingSlice/archiveServiceRequest",
  async (id: string) => {
    return serviceRequestsApi.archiveServiceRequest(id);
  }
);

export const unarchiveServiceRequest = createAsyncThunk(
  "customerServicingSlice/unarchiveServiceRequest",
  async (id: string) => {
    return serviceRequestsApi.unarchiveServiceRequest(id);
  }
);

export const reactivateServiceRequest = createAsyncThunk(
  "customerServicingSlice/reactivateServiceRequest",
  async (id: string) => {
    return serviceRequestsApi.reactivateServiceRequest(id);
  }
);

export const deleteServiceRequest = createAsyncThunk(
  "customerServicingSlice/deleteServiceRequest",
  async (id: string) => {
    return serviceRequestsApi.deleteServiceRequest(id);
  }
);

export const getServiceRequestStats = createAsyncThunk(
  "customerServicingSlice/getServiceRequestStats",
  async () => {
    return serviceRequestsApi.getStats();
  }
);

const serviceRequestsState: ServiceRequestsState = {
  serviceRequestsQuery: {
    statusCodes: [],
    pageSize: 10,
    pageNo: 1
  },
  serviceRequests: undefined,
  serviceRequestsFilters: undefined,
  serviceRequest: undefined,
  serviceRequestStats: undefined,
  loading: false,
  success: undefined,
  error: undefined
};

const customerServicingSlice = createSlice({
  name: "customerServicingSlice",
  initialState: serviceRequestsState,
  reducers: {
    changeServiceRequestsQuery: (state, action) => {
      state.serviceRequestsQuery = action.payload;
    },
    clearServiceRequest: (state) => {
      state.serviceRequest = undefined;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = undefined;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getServiceRequests.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServiceRequests.fulfilled, (state, { payload }) => {
        state.serviceRequests = payload;
        state.loading = false;
      })
      .addCase(getServiceRequests.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      .addCase(getServiceRequestsFilters.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServiceRequestsFilters.fulfilled, (state, { payload }) => {
        state.serviceRequestsFilters = payload;
        state.loading = false;
      })
      .addCase(getServiceRequestsFilters.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      .addCase(getServiceRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServiceRequest.fulfilled, (state, { payload }) => {
        state.serviceRequest = payload;
        state.loading = false;
      })
      .addCase(getServiceRequest.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      .addCase(createServiceRequest.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(createServiceRequest.fulfilled, (state, { payload }) => {
        state.serviceRequest = payload;
        state.loading = false;
      })
      .addCase(createServiceRequest.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      .addCase(changeServiceRequest.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(changeServiceRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changeServiceRequest.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      .addCase(publishServiceRequest.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(publishServiceRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(publishServiceRequest.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      .addCase(unpublishServiceRequest.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(unpublishServiceRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(unpublishServiceRequest.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      .addCase(archiveServiceRequest.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(archiveServiceRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(archiveServiceRequest.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      .addCase(unarchiveServiceRequest.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(unarchiveServiceRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(unarchiveServiceRequest.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      .addCase(reactivateServiceRequest.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(reactivateServiceRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(reactivateServiceRequest.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      .addCase(deleteServiceRequest.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(deleteServiceRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteServiceRequest.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // ─── Service Request Stats ────────────────────────────
      .addCase(getServiceRequestStats.fulfilled, (state, { payload }) => {
        state.serviceRequestStats = payload;
      });
  }
});

export const { changeServiceRequestsQuery, clearServiceRequest, setSuccess, setLoading, setError, clearError } =
  customerServicingSlice.actions;

export default customerServicingSlice.reducer;
